import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Phone, ShieldAlert, MapPin, CheckCircle2, XCircle, RefreshCw, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const STATUS_TONE = {
  CREATED: "bg-amber-100 text-amber-900", ALERTS_ATTEMPTED: "bg-amber-100 text-amber-900",
  ACKNOWLEDGED: "bg-emerald-100 text-emerald-900", RESOLVED: "bg-stone-200 text-stone-800", CANCELLED: "bg-stone-200 text-stone-800",
};
const STATUS_TEXT = {
  CREATED: "Alert saved — no one has confirmed yet",
  ALERTS_ATTEMPTED: "Contacts attempted — waiting for someone to acknowledge",
  ACKNOWLEDGED: "Acknowledged by a contact — help is aware",
  RESOLVED: "Resolved — you marked yourself safe",
  CANCELLED: "Cancelled by you",
};
const DELIVERY_TEXT = { simulated: "Simulated · not sent (SMS not configured)", failed: "Failed to send", queued: "Submitted to SMS provider", submitted: "Submitted to SMS provider" };

function CallBar({ cfg, contacts }) {
  const n = cfg?.emergency_number || "112";
  return (
    <div className="space-y-2">
      <a href={`tel:${n}`} data-testid="sos-call-112"
         className="flex items-center justify-center gap-3 h-16 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-2xl font-black shadow-lg transition-colors">
        <Phone size={26} /> Call {n}
      </a>
      {cfg && !cfg.emergency_verified && cfg.emergency_fallback && cfg.emergency_fallback !== n && (
        <a href={`tel:${cfg.emergency_fallback}`} data-testid="sos-call-fallback"
           className="flex items-center justify-center gap-2 h-12 rounded-xl bg-red-100 text-red-800 font-black">
          <Phone size={18} /> Also try {cfg.emergency_fallback} (number for this country not yet verified)
        </a>
      )}
      {contacts?.map((c) => (
        <a key={c.phone} href={`tel:${c.phone}`} data-testid={`sos-call-${c.role}`}
           className="flex items-center justify-between h-12 px-4 rounded-xl bg-white border border-stone-200 font-bold text-stone-800">
          <span>{c.role === "security" ? "Building security" : "Lift rescue"} · {c.name}</span><span dir="ltr">{c.phone}</span>
        </a>
      ))}
    </div>
  );
}

function Tracker({ inc, onChange }) {
  const close = async (kind) => {
    const reason = window.prompt(kind === "resolve" ? "Confirm you are safe. Add a short note:" : "Why cancel this alert? (required)", kind === "resolve" ? "I am out safely" : "");
    if (!reason || reason.trim().length < 3) return;
    try { onChange((await api.post(`/sos/incidents/${inc.incident_id}/${kind}`, { confirm: true, reason })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const active = ["CREATED", "ALERTS_ATTEMPTED", "ACKNOWLEDGED"].includes(inc.status);
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 space-y-3" data-testid="sos-tracker">
      <div className={`rounded-xl px-3 py-2 font-black text-sm ${STATUS_TONE[inc.status]}`} data-testid="sos-status">{STATUS_TEXT[inc.status]}</div>
      <p className="text-xs text-stone-500" data-testid="sos-no-dispatch-note">Find2Near has not contacted emergency services. Call {inc.emergency_number || "112"} yourself if you have not.</p>
      <ul className="space-y-1 text-sm" data-testid="sos-recipients">
        {inc.recipients.length === 0 && <li className="text-stone-500">No building or personal contacts configured.</li>}
        {inc.recipients.map((r) => (
          <li key={r.recipient_id} className="flex justify-between gap-2" data-testid={`sos-recipient-${r.recipient_id}`}>
            <span>{r.name} <span className="text-stone-400">({r.role.replace("_", " ")}) {r.to_masked}</span></span>
            <span className={r.acknowledged_at ? "text-emerald-700 font-bold" : "text-stone-600"}>
              {r.acknowledged_at ? "Acknowledged" : DELIVERY_TEXT[r.delivery?.status] || r.delivery?.status}
            </span>
          </li>
        ))}
      </ul>
      <ol className="text-xs text-stone-500 border-s-2 border-stone-200 ps-3 space-y-1" data-testid="sos-timeline">
        {inc.timeline.map((t, i) => <li key={i}><b>{new Date(t.at).toLocaleTimeString()}</b> · {t.note}</li>)}
      </ol>
      {active && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => close("resolve")} data-testid="sos-resolve-btn" className="h-10 px-4 rounded-full bg-emerald-600 text-white font-bold inline-flex items-center gap-1"><CheckCircle2 size={16}/> I'm safe</button>
          <button onClick={() => close("cancel")} data-testid="sos-cancel-btn" className="h-10 px-4 rounded-full border border-stone-300 font-bold inline-flex items-center gap-1"><XCircle size={16}/> Cancel alert</button>
          {inc.location_sharing && (
            <button onClick={async () => { await api.post(`/sos/incidents/${inc.incident_id}/stop-sharing`); onChange({ ...inc, location_sharing: false, location: null }); }}
                    data-testid="sos-stop-sharing-btn" className="h-10 px-4 rounded-full border border-stone-300 font-bold">Stop sharing location</button>
          )}
        </div>
      )}
    </div>
  );
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Location not available on this device"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy }),
      (e) => reject(e), { enableHighAccuracy: true, timeout: 10000 });
  });
}

function AlertForm({ lift, qr, onCreated }) {
  const [f, setF] = useState({ building: "", floor: "", lift_label: "", note: "", people_count: 1 });
  const [share, setShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let location = null;
      if (share) {
        try { location = await getPosition(); }
        catch { toast.error("Couldn't get your location — sending without it."); }
      }
      const r = await api.post("/sos/incidents", {
        kind: "lift_entrapment", qr_code: qr || undefined, ...f, people_count: Number(f.people_count) || undefined,
        share_location: !!location, location: location || undefined,
      });
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };
  const inp = "w-full h-11 rounded-xl border border-stone-200 px-3 text-sm";
  return (
    <form onSubmit={submit} className="rounded-2xl border border-stone-200 bg-white p-4 space-y-3" data-testid="sos-form">
      {lift ? (
        <div className="text-sm font-bold text-stone-800" data-testid="sos-lift-identified"><QrCode size={14} className="inline"/> {lift.building} · Lift {lift.lift_label}</div>
      ) : (
        <>
          <input className={inp} placeholder="Building / society name" value={f.building} onChange={(e) => setF({ ...f, building: e.target.value })} data-testid="sos-building-input" />
          <input className={inp} placeholder="Lift number or name (e.g. Lift B)" value={f.lift_label} onChange={(e) => setF({ ...f, lift_label: e.target.value })} data-testid="sos-lift-input" />
        </>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input className={inp} placeholder="Floor (approx.)" value={f.floor} onChange={(e) => setF({ ...f, floor: e.target.value })} data-testid="sos-floor-input" />
        <input className={inp} type="number" min="1" max="40" placeholder="People inside" value={f.people_count} onChange={(e) => setF({ ...f, people_count: e.target.value })} data-testid="sos-people-input" />
      </div>
      <textarea className={`${inp} h-20 py-2`} placeholder="Anything rescuers should know (medical needs, children…)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="sos-note-input" />
      <label className="flex items-start gap-2 text-sm text-stone-700">
        <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} data-testid="sos-share-location" className="mt-1" />
        <span><MapPin size={14} className="inline"/> Share my location with the people alerted (only for this alert; you can stop any time)</span>
      </label>
      <button disabled={busy} data-testid="sos-submit" className="w-full h-12 rounded-full bg-stone-900 text-white font-black disabled:opacity-60">
        {busy ? "Sending…" : "Alert building & my emergency contacts"}
      </button>
    </form>
  );
}

export default function LiftSOS() {
  const { user } = useAuth();
  const { country } = useI18n();
  const [params, setParams] = useSearchParams();
  const qr = params.get("qr");
  const [cfg, setCfg] = useState(null);
  const [lift, setLift] = useState(null);
  const [inc, setInc] = useState(null);

  useEffect(() => { api.get("/sos/config", { params: { country } }).then((r) => setCfg(r.data)).catch(() => {}); }, [country]);
  useEffect(() => { if (qr) api.get(`/sos/lift/${qr}`).then((r) => setLift(r.data)).catch(() => toast.error("Lift QR not recognised — enter details manually.", { id: "sos-bad-qr" })); }, [qr]);
  useEffect(() => {
    if (!user) return;
    const id = params.get("incident");
    if (id) { api.get(`/sos/incidents/${id}`).then((r) => setInc(r.data)).catch(() => {}); return; }
    // Reload/back during an emergency must not lose the tracker: reopen the newest active alert.
    api.get("/sos/incidents/mine").then((r) => {
      const act = r.data.items.find((x) => ["CREATED", "ALERTS_ATTEMPTED", "ACKNOWLEDGED"].includes(x.status));
      if (act) setInc(act);
    }).catch(() => {});
  }, [params, user]);
  const onCreated = (d) => { setInc(d); setParams({ ...(qr && { qr }), incident: d.incident_id }, { replace: true }); };
  const refresh = useCallback(() => { if (inc) api.get(`/sos/incidents/${inc.incident_id}`).then((r) => setInc(r.data)).catch(() => {}); }, [inc]);
  useEffect(() => {
    if (!inc || !["CREATED", "ALERTS_ATTEMPTED", "ACKNOWLEDGED"].includes(inc.status)) return;
    const tm = setInterval(refresh, 15000);
    return () => clearInterval(tm);
  }, [inc, refresh]);

  return (
    <div className="max-w-xl mx-auto px-4 py-6 space-y-4" data-testid="lift-sos-page">
      <div className="flex items-center gap-2 text-red-700 font-black"><ShieldAlert /> Stuck in a lift?</div>
      <CallBar cfg={cfg} contacts={lift?.contacts} />
      {inc ? (
        <>
          <Tracker inc={{ ...inc, emergency_number: cfg?.emergency_number }} onChange={setInc} />
          <button onClick={refresh} data-testid="sos-refresh" className="text-sm font-bold text-stone-600 inline-flex items-center gap-1"><RefreshCw size={14}/> Refresh status</button>
        </>
      ) : user ? (
        <AlertForm lift={lift} qr={qr} onCreated={onCreated} />
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white p-4 text-sm" data-testid="sos-login-hint">
          Calling works without an account. To alert your saved emergency contacts, <Link to="/login" className="font-bold text-red-700 underline">sign in</Link>.
        </div>
      )}
      <section className="rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="sos-safe-steps" lang="en">
        <h2 className="font-black text-amber-900 mb-2">While you wait</h2>
        <ol className="list-decimal ps-5 space-y-1 text-sm text-amber-950">{(cfg?.safe_lift_steps || []).map((s) => <li key={s}>{s}</li>)}</ol>
      </section>
      <section className="rounded-2xl bg-stone-100 p-4" data-testid="sos-poor-network" lang="en">
        <h2 className="font-black text-stone-900 mb-2">Weak network?</h2>
        <ul className="list-disc ps-5 space-y-1 text-sm text-stone-700">{(cfg?.poor_network || []).map((s) => <li key={s}>{s}</li>)}</ul>
      </section>
      <p className="text-xs text-stone-500">Manage your <Link to="/settings/emergency" className="underline">emergency contacts</Link>. Safety guidance is general and not yet professionally reviewed.</p>
    </div>
  );
}
